/*  Greatly Social – AIPostCreationModal  (v2.0 – modernized UI)
    All logic identical to v1.
-------------------------------------------------------------------- */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

export default function AIPostCreationModal ({ show, onHide, onSave }) {
  const navigate = useNavigate()

  const [prompt,       setPrompt]       = useState('')
  const [images,       setImages]       = useState([])
  const [selected,     setSelected]     = useState('')
  const [caption,      setCaption]      = useState('')
  const [loading,      setLoading]      = useState(false)
  const [error,        setError]        = useState('')
  const [outOfCredits, setOutOfCredits] = useState(false)

  const generate = async () => {
    if (!prompt.trim()) return
    try {
      setLoading(true); setError(''); setOutOfCredits(false); setImages([]); setSelected('')
      const { data } = await axios.post('/api/ai/generate-post', { prompt })
      setImages(data.images || [])
      setCaption(data.caption || '')
    } catch (err) {
      console.error(err)
      if (err.response?.status === 402) setOutOfCredits(true)
      else setError(err.response?.data?.error || 'Generation failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = () => {
    if (!selected) return
    onSave({ imageUrl: selected, caption })
  }

  const reset = () => {
    setPrompt(''); setImages([]); setSelected(''); setCaption(''); setError(''); setOutOfCredits(false)
  }

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={() => { reset(); onHide() }} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1050, backdropFilter:'blur(2px)' }} />

      {/* Panel */}
      <div style={{
        position    : 'fixed',
        top         : '50%',
        left        : '50%',
        transform   : 'translate(-50%, -50%)',
        zIndex      : 1051,
        background  : '#ffffff',
        borderRadius: 16,
        width       : 680,
        maxWidth    : 'calc(100vw - 32px)',
        maxHeight   : '90vh',
        display     : 'flex',
        flexDirection:'column',
        boxShadow   : '0 20px 60px rgba(0,0,0,0.18)',
        overflow    : 'hidden',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 14px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg, #7c3aed, #2563eb)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className='fas fa-magic' style={{ fontSize:13, color:'#fff' }} />
            </div>
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', margin:0 }}>✨ AI Post Creation</h3>
              <p style={{ fontSize:12, color:'#6b7280', margin:0 }}>Generate an image and caption with AI.</p>
            </div>
          </div>
          <button onClick={() => { reset(); onHide() }} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>

          {/* Prompt input */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>
              Describe the post you want
            </label>
            <div style={{ display:'flex', gap:8 }}>
              <input
                type='text'
                placeholder='e.g. "Boston Red Sox celebration"'
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                disabled={loading}
                onKeyDown={e => { if (e.key === 'Enter') generate() }}
                style={{
                  flex:1, padding:'9px 12px', borderRadius:8,
                  border:'1.5px solid #e5e7eb', fontSize:13, color:'#111827',
                  outline:'none', background: loading ? '#f9fafb' : '#ffffff',
                }}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={generate}
                disabled={loading || !prompt.trim()}
                style={{
                  padding:'9px 18px', borderRadius:8, border:'none',
                  background: (loading || !prompt.trim()) ? '#e5e7eb' : 'linear-gradient(135deg, #7c3aed, #2563eb)',
                  color: (loading || !prompt.trim()) ? '#9ca3af' : '#fff',
                  fontSize:13, fontWeight:700, cursor: (loading || !prompt.trim()) ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', gap:7, whiteSpace:'nowrap',
                }}
              >
                {loading
                  ? <><Spinner animation='border' size='sm' style={{ width:13, height:13 }} /> Generating…</>
                  : <><i className='fas fa-magic' style={{ fontSize:11 }} /> Generate</>
                }
              </button>
            </div>
          </div>

          {/* Out of credits */}
          {outOfCredits && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#dc2626', marginBottom:10 }}>You're out of AI credits.</div>
              <button
                onClick={() => { reset(); onHide(); navigate('/dashboard/credits') }}
                style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background:'#2563eb', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}
              >
                Buy Credits
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#dc2626' }}>
              <i className='fas fa-exclamation-circle me-2' />{error}
            </div>
          )}

          {/* Image grid */}
          {images.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:10 }}>
                Select an image
              </label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:10 }}>
                {images.map(url => {
                  const isSel = selected === url
                  return (
                    <div
                      key={url}
                      onClick={() => setSelected(url)}
                      style={{
                        position    : 'relative',
                        borderRadius: 10,
                        overflow    : 'hidden',
                        cursor      : 'pointer',
                        border      : `2px solid ${isSel ? '#2563eb' : 'transparent'}`,
                        boxShadow   : isSel ? '0 0 0 3px rgba(37,99,235,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                        aspectRatio : '1',
                        transition  : 'all 0.15s',
                      }}
                    >
                      <img src={url} alt='' style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                      {isSel && (
                        <div style={{ position:'absolute', inset:0, background:'rgba(37,99,235,0.25)', display:'flex', alignItems:'flex-start', justifyContent:'flex-start', padding:8 }}>
                          <div style={{ width:22, height:22, borderRadius:'50%', background:'#2563eb', display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid #fff' }}>
                            <i className='fas fa-check' style={{ fontSize:9, color:'#fff' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Caption editor */}
          {images.length > 0 && (
            <div>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Edit caption</label>
              <textarea
                rows={3}
                value={caption}
                onChange={e => setCaption(e.target.value)}
                style={{
                  width:'100%', padding:'9px 12px', borderRadius:8,
                  border:'1.5px solid #e5e7eb', fontSize:13, color:'#111827',
                  resize:'vertical', outline:'none', boxSizing:'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button onClick={() => { reset(); onHide() }} style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Cancel
          </button>
          <button
            disabled={!selected}
            onClick={() => { handleSave(); reset(); onHide() }}
            style={{
              padding:'8px 18px', borderRadius:8, border:'none',
              background: selected ? '#16a34a' : '#e5e7eb',
              color: selected ? '#fff' : '#9ca3af',
              fontSize:13, fontWeight:700, cursor: selected ? 'pointer' : 'not-allowed',
              display:'flex', alignItems:'center', gap:7,
            }}
          >
            <i className='fas fa-check' style={{ fontSize:11 }} /> Save to Post
          </button>
        </div>
      </div>
    </>
  )
}

AIPostCreationModal.propTypes = {
  show   : PropTypes.bool.isRequired,
  onHide : PropTypes.func.isRequired,
  onSave : PropTypes.func.isRequired,
}